/**
 * disputeFile.ts — "File Dispute" on a deal, against the real endpoint.
 *
 * WHAT WAS WRONG (card 1218889717010233, parent 1218886330579015). Blake's
 * #dispute-file handler (app.js) minted a "GBZ-…" number from the page's own
 * thread id, pushed a DISPUTES row, marked the deal "DISPUTE OPEN" and showed
 * "Dispute Filed" with no server call. The server never heard of it, and an
 * open ticket is the ONLY thing that holds escrow release
 * (escrowScheduler.ts hasOpenDisputeOrUnknown: any support_tickets row on the
 * offer that is not closed). So the buyer believed a case was open while the
 * seller's payout went out on schedule. #dispute-file stays in GUARDED_WRITES
 * as the fail-safe; this module does the real write alongside, the way
 * dealActions does, and like it never stops the click's propagation.
 *
 * WHICH OFFER. The list row's own offer id (__gbzListOfferId, via
 * __gbzDisputeContext), rewritten by every list read. Not the thread detail's
 * __gbzOfferId, which dealActions uses: that one is read once per tab, so after
 * a counter accepted in the app it names the superseded offer. A ticket on that
 * offer gets a 201 (the server refuses only a completed one) but holds nothing,
 * because escrow release checks tickets on the offer being released. Accept and
 * Decline fail loudly on a stale id; a dispute would not.
 *
 * THE REQUEST is the one the app's DisputeModal sends:
 *   POST /api/support/tickets
 *   { category: "offer", subject, body, listingId, offerId }
 * with the app's message shape (Blake, 2026-09-28): the subject names the reason
 * and the deal's reference (GBZ- plus 8 characters of the offer id, the
 * server's own gbzDealRef), and the body says who and which listing before the
 * person's words. The server's 40-character floor counts that whole body, so
 * Blake's 10-character minimum clears it exactly as the app's does. The subject
 * is what the OTHER person's notification quotes ("A dispute was opened on your
 * deal: ..."), so it carries the reason and the reference only. The case itself
 * is theirs to read, description included: GET /api/support/my-tickets returns
 * it to them without the filer's contact details, so they can respond. That is
 * the app's behavior too, since the ticket is the same.
 *
 * WHAT A PERSON SEES. On a 201 the form shows Blake's own "Dispute Filed" with
 * the server's case reference: GBZ-D- plus the first 8 characters of the ticket
 * id (mail.ts gbzCaseRef; the confirmation email shows the same 8 as
 * "Ticket #"). The deal is then re-read, so its "In review" state comes from the
 * server's open-dispute list. A refusal (a completed deal, a dispute already
 * open, a deal that is not yours, a suspended account, an expired session)
 * shows the server's own sentence, and the form keeps what was typed. Only a
 * failure the server gave no sentence for shows GENERIC_FAILURE_MESSAGE.
 */

import { UNRESOLVED_OFFER_MESSAGE } from "./dealActions";
import { installDealThread, refreshDeals } from "./floorTransport";

export const FILE_BUTTON = "#dispute-file";
export const REASON_CHIP = "[data-dreason]";
export const TEXT_FIELD = "#dispute-text";
export const TICKETS_ENDPOINT = "/api/support/tickets";
/** Blake's own minimum (app.js gate(): reason chosen and 10 characters). */
export const MIN_DETAILS = 10;
/** Approved by Blake 2026-09-28, for a failure the server gave no sentence for. */
export const GENERIC_FAILURE_MESSAGE = "Couldn't file the dispute — please try again";
/** Reused, not new: dealActions' approved line for a deal whose offer id has not loaded. */
export { UNRESOLVED_OFFER_MESSAGE };

/** The deal's reference, as the server's gbzDealRef and the app's DisputeModal write it. */
export const dealRef = (offerId: string): string => `GBZ-${offerId.slice(0, 8).toUpperCase()}`;
/** The case's reference, as the server's gbzCaseRef writes it. */
export const caseRef = (ticketId: string): string => `GBZ-D-${ticketId.slice(0, 8).toUpperCase()}`;

export interface DisputeContext {
  offerId: string;
  withHandle: string;
  listingId: string | null;
  listingTitle: string;
}

/** The ticket, in the app's shape. A pure function, so the wire is measured without a DOM. */
export function buildDisputeTicket(ctx: DisputeContext, reason: string, details: string) {
  const ref = dealRef(ctx.offerId);
  const about = ctx.listingId
    ? ` for listing "${ctx.listingTitle}" (listing ID: ${ctx.listingId}, ref ${ref})`
    : ` (ref ${ref})`;
  return {
    category: "offer" as const,
    subject: `Dispute: ${reason} — Ref ${ref}`,
    body: `Dispute against @${ctx.withHandle}${about}.\n\nReason: ${reason}\n\nDetails:\n${details}`,
    ...(ctx.listingId ? { listingId: ctx.listingId } : {}),
    offerId: ctx.offerId,
  };
}

type DisputeWindow = Window & {
  toast?: (message: string) => void;
  __gbzDisputeFor?: () => string | null;
  __gbzDisputeContext?: (
    threadId: string,
  ) => { withHandle: string; listingId: string | null; listingTitle: string; offerId: string | null } | null;
  __gbzDisputeFiled?: (threadId: string, ref: string) => boolean;
};

export function installDisputeFile(
  win: Window,
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
  myUserId: string,
  {
    notify = (message: string) => {
      if (!win.document.getElementById("toast")) return;
      (win as DisputeWindow).toast?.(message);
    },
  }: { notify?: (m: string) => void } = {},
): () => void {
  /* One request per deal at a time: a real await now sits behind the tap. */
  const busy = new Set<string>();

  async function file(threadId: string): Promise<void> {
    const w = win as DisputeWindow;
    const about = w.__gbzDisputeContext?.(threadId) ?? null;
    const offerId = about?.offerId ?? null;
    if (!about || !offerId) {
      notify(UNRESOLVED_OFFER_MESSAGE);
      return;
    }
    const chip = win.document.querySelector(`${REASON_CHIP}.on`);
    const reason = chip?.getAttribute("data-dreason") ?? "";
    const details = (win.document.querySelector<HTMLTextAreaElement>(TEXT_FIELD)?.value ?? "").trim();
    /* Blake's gate keeps the button disabled until both hold; reaching here
       without them means the form changed under the tap, so nothing is sent. */
    if (!reason || details.length < MIN_DETAILS) return;

    let res: Response;
    try {
      res = await authFetch(TICKETS_ENDPOINT, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(
          buildDisputeTicket(
            { offerId, withHandle: about.withHandle, listingId: about.listingId, listingTitle: about.listingTitle },
            reason,
            details,
          ),
        ),
      });
    } catch {
      notify(GENERIC_FAILURE_MESSAGE);
      return;
    }
    /* A 500 can be an HTML page; the parse must never throw inside a capture listener. */
    const body = (await res.json().catch(() => ({}))) as { id?: unknown; error?: unknown };
    if (!res.ok) {
      notify(typeof body.error === "string" && body.error ? body.error : GENERIC_FAILURE_MESSAGE);
      return;
    }
    /* Filed. The server returns the ticket; its id makes the case reference. A
       201 with no id is still a filed ticket, so the deal's own reference
       stands in rather than a failure that invites a second filing. */
    const ref = typeof body.id === "string" && body.id ? caseRef(body.id) : dealRef(offerId);
    /* The ticket exists now; a repaint that throws must not skip the re-read,
       or the deal keeps its "Open a dispute" link until a reload. */
    try {
      w.__gbzDisputeFiled?.(threadId, ref);
    } catch {
      /* the re-read below paints the deal from the server either way */
    }
    await refreshDeals(win, authFetch, myUserId).catch(() => null);
    await installDealThread(win, authFetch, threadId, myUserId).catch(() => false);
  }

  const onClick = (event: Event): void => {
    const button = (event.target as Element | null)?.closest?.(FILE_BUTTON);
    if (!button || (button as HTMLButtonElement).disabled) return;
    const threadId = (win as DisputeWindow).__gbzDisputeFor?.() ?? null;
    if (!threadId || busy.has(threadId)) return;
    busy.add(threadId);
    void file(threadId).finally(() => busy.delete(threadId));
  };

  win.document.addEventListener("click", onClick, true);
  return () => win.document.removeEventListener("click", onClick, true);
}
